import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { PatientRoles, Role } from '../../config/DiactiveTypes';
import { getAllIncidents } from '../../services/IncidentService';
import { getPatientBasicInfo, getUserFullName } from '../../services/UserService';
import { formatDateTime, getPatientGroupName, getPatientHspId } from '../../utils/DiactiveUtils';
import HeaderFilterSelect from '../common/HeaderFilterSelect';

const IncidentStatusFilter = {
  All: 'all',
  Open: 'open',
  Resolved: 'resolved',
};

const IncidentGroupFilter = {
  All: 'all',
  Control: 'control',
  Intervention: 'intervention',
};

const AllIncidentsTable = ({ role }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [sortDir, setSortDir] = useState('desc');

  const [selectedStatus, setSelectedStatus] = useState(() => {
    const s = searchParams.get('status');
    return Object.values(IncidentStatusFilter).includes(s) ? s : IncidentStatusFilter.Open;
  });
  const [selectedGroup, setSelectedGroup] = useState(() => {
    const g = searchParams.get('group');
    return Object.values(IncidentGroupFilter).includes(g) ? g : IncidentGroupFilter.All;
  });

  const refreshItems = async () => {
    setLoading(true);
    try {
      const incidents = await getAllIncidents();

      const uniquePatientIds = [...new Set(incidents.map((i) => i.patientId).filter(Boolean))];
      const patientInfoEntries = await Promise.all(
        uniquePatientIds.map(async (id) => {
          try {
            const info = await getPatientBasicInfo(id);
            return [id, info];
          } catch {
            return [id, null];
          }
        })
      );
      const patientInfoById = Object.fromEntries(patientInfoEntries);

      const uniqueUserIds = [
        ...new Set([
          ...incidents.map((i) => i.createdBy),
          ...incidents.map((i) => i.resolvedBy),
        ].filter(Boolean))
      ];
      const userNameEntries = await Promise.all(
        uniqueUserIds.map(async (id) => {
          try {
            const name = await getUserFullName(id);
            return [id, name];
          } catch {
            return [id, null];
          }
        })
      );
      const userNamesById = Object.fromEntries(userNameEntries);

      const enriched = incidents.map((incident) => {
        const patient = patientInfoById[incident.patientId] ?? null;
        const hspId =
          patient?.patientNumber && patient?.hospital ? getPatientHspId(patient) : null;
        return {
          ...incident,
          patientName: patient?.fullName ?? null,
          patientNickname: patient?.nickname ?? null,
          patientRole: patient?.role ?? null,
          patientHspId: hspId,
          createdByName: incident.createdBy ? (userNamesById[incident.createdBy] ?? null) : null,
          resolvedByName: incident.resolvedBy ? (userNamesById[incident.resolvedBy] ?? null) : null,
        };
      }).filter((item) => PatientRoles.includes(item.patientRole));

      setItems(enriched);
    } catch (e) {
      console.error('AllIncidentsTable: failed to load incidents', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('status', selectedStatus);
      next.set('group', selectedGroup);
      return next;
    }, { replace: true });
  }, [selectedStatus, selectedGroup, setSearchParams]);

  const patientDisplay = (row) => {
    if (role === Role.Admin) {
      return row?.patientNickname ?? row?.patientName ?? '—';
    }
    return row?.patientName ?? '—';
  };

  const itemsForStatusCounts = items.filter((item) => {
    if (selectedGroup === IncidentGroupFilter.All) return true;
    if (selectedGroup === IncidentGroupFilter.Control) return item?.patientRole === Role.Ctrl25;
    if (selectedGroup === IncidentGroupFilter.Intervention) return item?.patientRole === Role.Intr25;
    return true;
  });
  const itemsForGroupCounts = items.filter((item) => {
    if (selectedStatus === IncidentStatusFilter.All) return true;
    return selectedStatus === IncidentStatusFilter.Resolved
      ? item?.status === 'resolved'
      : item?.status !== 'resolved';
  });

  const statusCounts = {
    all: itemsForStatusCounts.length,
    open: itemsForStatusCounts.filter((item) => item?.status !== 'resolved').length,
    resolved: itemsForStatusCounts.filter((item) => item?.status === 'resolved').length,
  };
  const groupCounts = {
    all: itemsForGroupCounts.length,
    control: itemsForGroupCounts.filter((item) => item?.patientRole === Role.Ctrl25).length,
    intervention: itemsForGroupCounts.filter((item) => item?.patientRole === Role.Intr25).length,
  };

  const statusOptions = [
    { value: IncidentStatusFilter.Open, label: 'Abiertas', count: statusCounts.open },
    { value: IncidentStatusFilter.Resolved, label: 'Resueltas', count: statusCounts.resolved },
  ];
  const groupOptions = [
    { value: IncidentGroupFilter.Control, label: 'Control', count: groupCounts.control },
    { value: IncidentGroupFilter.Intervention, label: 'Intervención', count: groupCounts.intervention },
  ];

  const filteredItems = items.filter((item) => {
    let matchesStatus = true;
    if (selectedStatus !== IncidentStatusFilter.All) {
      const isResolved = item?.status === 'resolved';
      matchesStatus = selectedStatus === IncidentStatusFilter.Resolved ? isResolved : !isResolved;
    }
    let matchesGroup = true;
    if (selectedGroup !== IncidentGroupFilter.All) {
      if (selectedGroup === IncidentGroupFilter.Control) matchesGroup = item?.patientRole === Role.Ctrl25;
      if (selectedGroup === IncidentGroupFilter.Intervention) matchesGroup = item?.patientRole === Role.Intr25;
    }
    return matchesStatus && matchesGroup;
  });

  const sortedItems = [...filteredItems].sort((a, b) => {
    const aMs = a.createdAt?.toDate?.()?.getTime() ?? 0;
    const bMs = b.createdAt?.toDate?.()?.getTime() ?? 0;
    return sortDir === 'asc' ? aMs - bMs : bMs - aMs;
  });

  const renderStatusCell = (row) => {
    if (row?.status === 'resolved') {
      const tooltipTitle = (
        <Stack spacing={0.5}>
          <Typography variant="caption">
            Resuelto por: {row?.resolvedByName ?? row?.resolvedBy ?? '—'}
          </Typography>
          <Typography variant="caption">{formatDateTime(row?.resolvedAt)}</Typography>
        </Stack>
      );
      return (
        <Tooltip title={tooltipTitle} arrow>
          <Typography variant="body2" color="success.main" sx={{ width: 'fit-content' }}>
            Resuelta
          </Typography>
        </Tooltip>
      );
    }
    return <Typography variant="body2">Abierta</Typography>;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedStatusForSelect = selectedStatus === IncidentStatusFilter.All ? '' : selectedStatus;
  const selectedGroupForSelect = selectedGroup === IncidentGroupFilter.All ? '' : selectedGroup;

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 180, minWidth: 180 }}>
                <TableSortLabel
                  active
                  direction={sortDir}
                  onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                >
                  Fecha
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: 240, minWidth: 240 }}>Paciente</TableCell>
              <TableCell sx={{ width: 160, minWidth: 160 }}>
                <HeaderFilterSelect
                  label="Grupo"
                  value={selectedGroupForSelect}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedGroup(v === '' ? IncidentGroupFilter.All : v);
                  }}
                  options={groupOptions}
                  totalCount={itemsForGroupCounts.length}
                  minWidth={0}
                />
              </TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell sx={{ width: 200, minWidth: 200 }}>Creada por</TableCell>
              <TableCell sx={{ width: 180, minWidth: 180 }}>
                <HeaderFilterSelect
                  label="Estado"
                  value={selectedStatusForSelect}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedStatus(v === '' ? IncidentStatusFilter.All : v);
                  }}
                  options={statusOptions}
                  totalCount={itemsForStatusCounts.length}
                  allLabel="Todas"
                  minWidth={0}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedItems.map((row) => (
              <TableRow
                key={`${row.patientId}-${row.id}`}
                hover
                onClick={() => navigate(`/patient/view/${row.patientId}/incidents`, { state: { fromFilteredTable: true } })}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ py: 1.5 }}>{formatDateTime(row.createdAt)}</TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2">{patientDisplay(row)}</Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2">{getPatientGroupName(row.patientRole) || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Tooltip title={row.text} placement="top" enterDelay={300}>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 380,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.text}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2">{row.createdByName ?? row.createdBy ?? '—'}</Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>{renderStatusCell(row)}</TableCell>
              </TableRow>
            ))}
            {sortedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No hay incidencias.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default AllIncidentsTable;
